import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../utils/api';
import { useIsEnterpriseAdmin } from '../hooks/useIsEnterpriseAdmin';
import { useWorkspace } from '../contexts/WorkspaceContext';
import HubPageShell from '../components/home/HubPageShell';
import styles from './UpgradePage.module.css';

function CapacityLine({ label, used, total }) {
  return (
    <p className={styles.currentPlan}>
      {label}: <strong>{used ?? 0}</strong> / {total ?? 0}
    </p>
  );
}

export default function BillingPage() {
  const { isEnterpriseAdmin, isEnterpriseUser, loading: roleLoading } = useIsEnterpriseAdmin();
  const { current } = useWorkspace();
  const [status, setStatus] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [error, setError] = useState(null);
  const [portalMessage, setPortalMessage] = useState(null);

  const inCompany = current?.type === 'company' && !!current.id;
  const showCompanyBilling = inCompany && !!(current.isAdmin || current.isOwner);

  useEffect(() => {
    if (roleLoading) return undefined;
    if (isEnterpriseUser && !isEnterpriseAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (showCompanyBilling) {
          const [statusRes, capRes] = await Promise.all([
            api.get('/api/billing/status').catch(() => null),
            api.get('/api/billing/capacity', { params: { enterpriseId: current.id } }),
          ]);
          if (cancelled) return;
          if (statusRes) setStatus(statusRes.data);
          setCapacity(capRes.data.capacity || null);
        } else {
          const res = await api.get('/api/billing/status');
          if (!cancelled) setStatus(res.data);
        }
      } catch {
        if (!cancelled) setError('Could not load billing status.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roleLoading, isEnterpriseUser, isEnterpriseAdmin, showCompanyBilling, current?.id]);

  const openPortal = async () => {
    setPortalMessage(null);
    try {
      const res = await api.post(
        '/api/billing/portal',
        showCompanyBilling ? { enterpriseId: current.id } : {}
      );
      if (res.data?.url) {
        window.location.assign(res.data.url);
        return;
      }
      setPortalMessage('Billing portal is not available yet.');
    } catch (e) {
      setPortalMessage(
        e.response?.data?.message ||
          'Billing recovery is ready as a path, but Stripe Checkout/Portal is not enabled yet.'
      );
    }
  };

  const stripe = status?.stripe;
  const restricted = status?.entitlements?.restricted;
  const members = capacity?.members || {};
  const proLicences = capacity?.proLicences || {};

  if (!roleLoading && isEnterpriseUser && !isEnterpriseAdmin) {
    return <Navigate to="/upgrade" replace />;
  }

  return (
    <HubPageShell title="Billing">
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Billing</h1>
        {restricted ? (
          <p className={styles.currentPlan}>
            This account is restricted. You can still pay an overdue invoice or reactivate from here
            once Stripe test checkout is enabled.
          </p>
        ) : null}
      </header>

      {error ? <p className={styles.error}>{error}</p> : null}

      {showCompanyBilling ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Company capacity</h2>
          <p className={styles.sectionBody}>
            Member capacity is how many people can be in {current.name || 'this workspace'}.
            Pro licences are an overlay.
          </p>
          <CapacityLine
            label="Member capacity"
            used={members.used}
            total={members.total}
          />
          <CapacityLine
            label="Pro licences"
            used={proLicences.assigned}
            total={proLicences.total}
          />
          <button type="button" className={styles.back} onClick={openPortal}>
            Update billing
          </button>
          {portalMessage ? <p className={styles.cardNote}>{portalMessage}</p> : null}
        </section>
      ) : null}

      {!showCompanyBilling ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Subscription</h2>
          {stripe && !stripe.chargingEnabled ? (
            <p className={styles.sectionBody}>
              Billing portal opens when subscription billing is enabled. A quote is not a charge.
            </p>
          ) : (
            <p className={styles.sectionBody}>
              Personal Pro is billed here. Teams billing lives on the workspace.
            </p>
          )}
          <button type="button" className={styles.back} onClick={openPortal}>
            Update billing
          </button>
          {portalMessage ? <p className={styles.cardNote}>{portalMessage}</p> : null}
        </section>
      ) : null}
    </div>
    </HubPageShell>
  );
}
